import Promise from 'bluebird';
import _ from 'lodash';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import ColorBrewer from '../core/tools/colorbrewer';
import environment from '../environment';

import HaloLayer from './HaloLayer';
import Leaflet from './Leaflet';
import { VehicleTypeEnum } from '../core/vehicleType/constants';

const LayerControls = (name) => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

const countryGreenfieldYears = {
    'CO': '2028+'
};

export const NETWORK_ICON = 'network';

export const NETWORK_ICON_SIZE = [29, 29];

export const getNetworkPinUrl = () => {
    const url = environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto
        ? `${environment.get('IMAGES_HOST')}/motos`
        : `${environment.get('IMAGES_HOST')}`;

    return `${url}/pin_${String(environment.get('CONFIG.CLIENT')).toUnderscoreCase()}_xs.png`;
};

export const getNetworkSymbology = () => ({
    value: NETWORK_ICON,
    style: {
        icon: {
            iconUrl: getNetworkPinUrl(),
            iconSize: NETWORK_ICON_SIZE,
            iconAnchor: [15, 15],
            labelAnchor: [15, -15]
        },
        zIndexOffset: 100000,
        riseOnHover: true
    }
});

class GreenfieldLayer {
    constructor (currentMap) {
        const mapId = +currentMap._id;

        const clusterImage = new Image();
        clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_greenfield.png`;

        const onClick = (feature, layer) => {
            LayerControls('Greenfield').highlight(layer);
            LayerControls('Greenfield').show({
                show: true,
                id: feature.properties.Id
            });

            return new HaloLayer(currentMap, layer, feature, 'Greenfield', 'points');
        };

        const onSuccess = (res) => {
            const noPriorityType = -1;
            const shapeLayer = get('GreenfieldArea', currentMap);
            if (!shapeLayer.visible()) {
                shapeLayer.redraw();
            }

            return new Promise((resolve) => {
                _.forEach(res.features, f => {
                    const iconType = f.properties.TIPO_ICONE.toLowerCase();
                    const isMultiBrand = f.properties.IC_MTI_BND;

                    f.properties['Icon'] = isMultiBrand === 2 && iconType.includes('to')
                        ? 'pi_2' : isMultiBrand === 2
                            ? 'triangle' : iconType.includes('to')
                                ? 'pi' : 'circle';

                    f.properties['IsNetwork'] = Boolean(_.isNil(f.properties.IsNetwork)
                        ? f.properties.isNetwork
                        : f.properties.IsNetwork);

                    const action = _.isNil(f.properties.Action)
                        ? f.properties.action
                        : f.properties.Action;

                    f.properties['Action'] = _.isNil(action) ? '' : action;

                    if (f.properties.IsNetwork) {
                        f.properties['Icon'] = NETWORK_ICON;
                    }

                    const tierType = {
                      1: 'Throughput',
                      2: 'Market-Share',
                      3: 'Convenience',
                      4: 'Convenience',
                      5: 'Interest Point',
                      6: 'Interest Point'
                    };

                    if (isMultiBrand === 2 && iconType.includes('to')) {
                      f.properties['Legend'] = `Multi ${tierType[f.properties.Type.Id]}`;
                    }

                    const countryYear = countryGreenfieldYears[window.NeoGIG.country] || '2026+';

                    if (isMultiBrand === 2 && !iconType.includes('to')) {
                      f.properties['Legend'] = `${countryYear} Convenience Maximization (Tier ${f.properties.Type.Id})`;
                    }

                    if (iconType.includes('to') && isMultiBrand !== 2) {
                      f.properties['Legend'] = `${tierType[f.properties.Type.Id]} Maximization (Tier ${f.properties.Type.Id})`;
                    }

                    if (isMultiBrand !== 2 && !iconType.includes('to')) {
                      f.properties['Legend'] = `${f.properties.TIPO_ICONE} ${tierType[f.properties.Type.Id]} Maximization (Tier ${f.properties.Type.Id})`;
                    }

                    if (f.properties.Type.Id === 5) {
                      environment.get("CONFIG.CLIENT").toLowerCase() === 'gm' && f.properties.FilterGreenfieldVehicleType === 2
                        ? f.properties['Legend'] = `Priority - Service Interest Point (Under Decision)`
                        : f.properties['Legend'] = `After Sales - Interest Point (Tier ${f.properties.Type.Id})`;
                    }

                    if (f.properties.Type.Id === 6) {
                        f.properties['Legend'] = `Non Priority - Service Interest Point (Under Decision)`;
                    }

                    const tierColors = {
                        [1]: 'green',
                        [2]: 'blue',
                        [3]: 'red',
                        [5]: 'brown',
                        [6]: 'orange'
                    };

                    f.properties['TierColor'] = tierColors[f.properties.Type.Id] || 'purple';

                    if (f.properties.Priority.Id !== noPriorityType) {
                        f.properties[`Priority${f.properties.Priority.Name.toLowerCase().replace(/ /, '')}`] =
                            f.properties.Priority.Name;
                    }
                });
                resolve(res);
            });
        };

        const onComplete = (features) => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { LayerControls('Voronoi').update(mapId); }
            } catch (error) {
                console.log('Layer not avaliable');
            }

            return LayerControls('Greenfield').show({
                show: false,
                refresh: true
            });
        };

        const onRemove = (features) => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { return LayerControls('Voronoi').update(mapId); }

                const shapeLayer = get('GreenfieldArea', currentMap);
                if (shapeLayer.visible) {
                    shapeLayer.hide();
                }
            } catch (error) {
                console.log('Layer not avaliable');
            }
        };

        const onMoveend = () =>
            LayerControls('Greenfield').show({ refresh: true }, get('Greenfield', currentMap).getCurrentFeatures());

        const clusterTemplate = (cluster) => {
            const data = { value: cluster.getChildCount() };

            return pinBuilder.create.pin.imageWithValue(data, clusterImage);
        };

        return new Leaflet.GeoJSONLayer({
            map: currentMap,
            remote: {
                method: 'GET',
                url: `${environment.get('API_HOST')}/greenfield`,
                params: {
                    greenFieldType: environment.get('CONFIG.FILTERS.GREENFIELD_TYPES').length > 0
                        ? environment.get('CONFIG.FILTERS.GREENFIELD_TYPES')[0].id
                        : null,
                }
            },
            scaleRange: [5, 20],
            filter: {
                location: true
            },
            showAll: true,
            autofocus: false,
            uniqueField: 'Id',
            sort: 'IdType',
            click: onClick,
            remove: onRemove,
            dragend: onMoveend,
            zoomend: onMoveend,
            done: onComplete,
            success: onSuccess,
            legend: {
                title: 'Greenfield',
                text: '{Legend}',
                icon: properties => properties.IsNetwork
                    ? getNetworkPinUrl()
                    : `${environment.get('IMAGES_HOST')}/pin_${String(properties.Icon).toUnderscoreCase()}_${String(properties.TierColor).toUnderscoreCase()}_xs.png`,
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    template: clusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors('Greens')
                },
                property: 'Icon',
                values: [{
                    value: 'circle',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_circle_{TierColor}.png`,
                            iconSize: [29, 29], // size of the icon
                            iconAnchor: [15, 15], // point of the icon which will correspond to marker's location
                            labelAnchor: [15, -15]
                        },
                        riseOnHover: true
                    }
                }, {
                    value: 'triangle',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_triangle_{TierColor}.png`,
                            iconSize: [29, 29], // size of the icon
                            iconAnchor: [15, 15], // point of the icon which will correspond to marker's location
                            labelAnchor: [15, -15]
                        },
                        riseOnHover: true
                    }
                }, {
                    value: 'pi',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}.png`,
                            iconSize: [50, 64], // size of the icon
                            iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                            labelAnchor: [25, -32]
                        },
                        riseOnHover: true
                    }
                }, {
                    value: 'pi_2',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_2_{TierColor}.png`,
                            iconSize: [50, 64], // size of the icon
                            iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                            labelAnchor: [25, -32]
                        },
                        riseOnHover: true
                    }
                }, getNetworkSymbology()]
            }
        });
    }
}

export default GreenfieldLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/GreenfieldLayer.js